import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LOCATION_ORIGIN } from '../../../call-api';
import { getCurrentUser } from '../../../../redux-modules/current-user';
import { getCustomer } from '../../../../redux-modules/customers';
import {
  getOrganization,
  getOrgWithMemberStatus,
} from '../../../../redux-modules/organizations';
import { findLogoutItem, buildLoginHref } from '../headerLoginLogoutUtils';
import nfibHeaderHtml from './nfibHeaderHtmlString';
import { JoinRenewCta, computeJoinRenewCta } from './nfibJoinRenewUtils';
import { getNfibPublicThemeCssUrl, getMontserratFontsCssUrl } from './navConfig';
import {
  NFIB_PUBLIC_SITE_ORIGIN,
  rewriteNfibPublicSiteUrlsInHtml,
} from './nfibPublicSite';
import {
  isEngageNfibLoginPath,
  isCurrentOriginLoginPath,
  rewriteProductionEngageHostToOrigin,
} from './engageEnvLinks';

export { getNfibPublicThemeCssUrl, getMontserratFontsCssUrl };

const XL_BREAK = 1280;

function patchNfibShadowLinks(shadowRoot, { useLogout, logoutHref, loginHref }) {
  const base = NFIB_PUBLIC_SITE_ORIGIN;

  shadowRoot.querySelectorAll('a[href]').forEach(a => {
    const raw = (a.getAttribute('href') || '').trim();
    if (!raw) {
      return;
    }

    const isLogin =
      isEngageNfibLoginPath(raw) || isCurrentOriginLoginPath(raw, LOCATION_ORIGIN);

    if (useLogout && logoutHref && isLogin) {
      a.setAttribute('href', logoutHref);
      a.querySelectorAll('span').forEach(sp => {
        if (/login/i.test(sp.textContent.trim())) {
          sp.textContent = sp.textContent.replace(/login/gi, 'Logout');
        }
      });
      return;
    }

    if (!useLogout && isLogin) {
      a.setAttribute('href', loginHref);
      return;
    }

    if (raw.startsWith('/') && !raw.startsWith('//')) {
      a.setAttribute('href', base + raw);
    }
  });
}

function patchHeaderJoinRenewCtas(shadowRoot, { cta, userId }) {
  const base = NFIB_PUBLIC_SITE_ORIGIN;
  const joinHref = `${base}/join-now`;
  const renewHref = `${base}/renew-now`;
  const showRenew = !!userId && cta === JoinRenewCta.Renew;
  const hideCta = !!userId && cta === JoinRenewCta.None;

  shadowRoot
    .querySelectorAll(
      'a[href*="join-now"], a[href*="renew-now"], a[href*="renewnow"]'
    )
    .forEach(a => {
      const raw = (a.getAttribute('href') || '').trim();
      const isMembershipCta = /join-now|renew-?now/i.test(raw);
      if (!isMembershipCta) {
        return;
      }

      // member is within their current paid-up window - no CTA to show
      a.style.display = hideCta ? 'none' : '';
      if (hideCta) {
        return;
      }

      if (showRenew) {
        a.setAttribute('href', renewHref);
      } else {
        a.setAttribute('href', joinHref);
      }

      a.querySelectorAll('span').forEach(sp => {
        const t = sp.textContent;
        if (showRenew) {
          if (/join\s*now/i.test(t)) {
            sp.textContent = t.replace(/join\s*now/gi, 'Renew');
          }
        } else if (/\brenew\b/i.test(t)) {
          sp.textContent = t.replace(/\brenew\b/gi, 'Join Now');
        }
      });
    });
}

const MY_NFIB_LINK_ATTR = 'data-engage-mynfib-link';
const MY_NFIB_CELL_ATTR = 'data-engage-mynfib-cell';

/** Same structure as bundled Careers: outer toolbar cell + anchor + two responsive spans. */
function buildMyNfibToolbarCell(accountHref, cellClassName) {
  const cell = document.createElement('div');
  cell.setAttribute(MY_NFIB_CELL_ATTR, '1');
  cell.className = cellClassName;

  const anchor = document.createElement('a');
  anchor.setAttribute(MY_NFIB_LINK_ATTR, '1');
  anchor.setAttribute('href', accountHref);
  anchor.setAttribute('alt', 'MyNFIB');

  const span1 = document.createElement('span');
  span1.className =
    'text-[16px] leading-[24px] tracking-[0px] font-bold text-white xl:hidden ';
  span1.setAttribute('onClick', '');
  span1.textContent = 'MyNFIB';

  const span2 = document.createElement('span');
  span2.className =
    'text-[11px] leading-[16px] tracking-[0px] text-white hidden xl:block';
  span2.setAttribute('onClick', '');
  span2.textContent = 'MyNFIB';

  anchor.appendChild(span1);
  anchor.appendChild(span2);
  cell.appendChild(anchor);
  return cell;
}

/**
 * Inserts a MyNFIB toolbar cell like Careers: sibling
 * `<div class="flex border-l …">` (or mobile `border-t` row) before the Login/Logout cell.
 */
function insertMyNfibBeforeAuthAnchors(shadowRoot) {
  const origin = (LOCATION_ORIGIN || '').replace(/\/$/, '');
  if (!shadowRoot || !origin) {
    return;
  }
  const accountHref = `${origin}/account`;

  shadowRoot.querySelectorAll('[data-engage-mynfib-sep]').forEach(el => {
    el.remove();
  });

  shadowRoot.querySelectorAll('a[href]').forEach(a => {
    const raw = (a.getAttribute('href') || '').trim();
    const isLogin =
      isEngageNfibLoginPath(raw) || isCurrentOriginLoginPath(raw, LOCATION_ORIGIN);
    let isLogout = false;
    try {
      const u = new URL(raw, origin);
      isLogout =
        u.origin === origin && u.pathname.indexOf('/auth/logout') !== -1;
    } catch (e) {
      /* ignore malformed href */
    }
    if (!isLogin && !isLogout) {
      return;
    }

    const loginCell = a.parentElement;
    if (!loginCell || !loginCell.parentNode) {
      return;
    }

    const rowParent = loginCell.parentNode;

    const prevCell = loginCell.previousElementSibling;
    if (prevCell && prevCell.getAttribute(MY_NFIB_CELL_ATTR) === '1') {
      const link = prevCell.querySelector(`a[${MY_NFIB_LINK_ATTR}]`);
      if (link) {
        link.setAttribute('href', accountHref);
      }
      return;
    }

    const inlinePrev = a.previousElementSibling;
    if (
      inlinePrev &&
      inlinePrev.tagName === 'A' &&
      inlinePrev.getAttribute(MY_NFIB_LINK_ATTR) === '1'
    ) {
      inlinePrev.remove();
    }

    rowParent.insertBefore(
      buildMyNfibToolbarCell(accountHref, loginCell.className),
      loginCell
    );
  });
}

function bindNfibShadowEvents(shadowRoot) {
  const root = shadowRoot;
  const mobileTray = () => root.querySelector('.nav-mobile-container');
  const menuBtn = () =>
    root.querySelector('#nfib-mobile-menu-trigger') ||
    root.querySelector('.nfib-mobile-menu-trigger');

  /** Main charcoal row with logo + hamburger (bundled markup). */
  const mainNavBar = () => {
    const trigger = menuBtn();
    return trigger ? trigger.closest('div.flex.items-center') : null;
  };

  const syncMobileTrayLayout = () => {
    const tray = mobileTray();
    const bar = mainNavBar();
    if (!tray || !bar) {
      return;
    }
    const topPx = Math.ceil(bar.getBoundingClientRect().bottom);
    tray.style.setProperty('--nfib-mobile-tray-top', `${topPx}px`);
  };

  const backdrop = document.createElement('div');
  backdrop.className = 'nfib-mobile-tray-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  root.appendChild(backdrop);

  const closeMobileTray = () => {
    const tray = mobileTray();
    if (tray) {
      tray.classList.add('hidden');
      tray.classList.remove('flex');
    }
    backdrop.classList.remove('block');
  };

  const openMobileTray = () => {
    syncMobileTrayLayout();
    const tray = mobileTray();
    if (tray) {
      tray.classList.remove('hidden');
      tray.classList.add('flex');
    }
    backdrop.classList.add('block');
  };

  const toggleMobileTray = () => {
    const tray = mobileTray();
    if (!tray) {
      return;
    }
    if (tray.classList.contains('hidden')) {
      openMobileTray();
    } else {
      closeMobileTray();
    }
  };

  const onMenuClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleMobileTray();
  };

  const mb = menuBtn();
  if (mb) {
    mb.addEventListener('click', onMenuClick);
  }

  backdrop.addEventListener('click', () => {
    closeMobileTray();
  });

  const onKeyDown = e => {
    if (e.key !== 'Escape') {
      return;
    }
    const trayEl = mobileTray();
    if (trayEl && !trayEl.classList.contains('hidden')) {
      closeMobileTray();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  const onResize = () => {
    syncMobileTrayLayout();
    if (window.innerWidth >= XL_BREAK) {
      closeMobileTray();
    }
  };
  window.addEventListener('resize', onResize);
  syncMobileTrayLayout();

  const searchBtn = root.querySelector('.nfib-mobile-search-btn');
  const onSearch = e => {
    e.preventDefault();
    const input = root.querySelector('#mobile-search-input');
    const q = (input && input.value) || '';
    window.location.href = `${NFIB_PUBLIC_SITE_ORIGIN}/?s=${encodeURIComponent(
      q.trim()
    )}`;
  };
  if (searchBtn) {
    searchBtn.addEventListener('click', onSearch);
  }

  const accordionHandlers = [];
  root.querySelectorAll('.nav-mobile-container .nav-mobile').forEach(nav => {
    const row = nav.children[0];
    const menu = nav.querySelector('.nav-menu');
    if (!row || !menu) {
      return;
    }
    const fn = ev => {
      ev.preventDefault();
      menu.classList.toggle('hidden');
    };
    row.addEventListener('click', fn);
    accordionHandlers.push({ row, fn });
  });

  return () => {
    if (mb) {
      mb.removeEventListener('click', onMenuClick);
    }
    window.removeEventListener('resize', onResize);
    window.removeEventListener('keydown', onKeyDown);
    if (searchBtn) {
      searchBtn.removeEventListener('click', onSearch);
    }
    accordionHandlers.forEach(({ row, fn }) =>
      row.removeEventListener('click', fn)
    );
    backdrop.remove();
  };
}

export class NfibComHeader extends React.Component {
  static propTypes = {
    menuItems: PropTypes.array,
    showMenu: PropTypes.bool,
    docTitle: PropTypes.string,
    joinRenewCta: PropTypes.oneOf([
      JoinRenewCta.Join,
      JoinRenewCta.Renew,
      JoinRenewCta.None,
    ]),
    renewCustomerId: PropTypes.string,
  };

  static defaultProps = {
    menuItems: [],
    showMenu: true,
    docTitle: '',
    joinRenewCta: JoinRenewCta.None,
    renewCustomerId: null,
  };

  constructor(props) {
    super(props);
    /** @type {HTMLDivElement | null} */
    this.hostNode = null;
    this._isMounted = false;
    this.cleanupEvents = null;
    this.setHostRef = this.setHostRef.bind(this);
  }

  setHostRef(el) {
    this.hostNode = el;
  }

  componentDidMount() {
    this._isMounted = true;
    this.mountShadow();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.menuItems !== this.props.menuItems ||
      prevProps.showMenu !== this.props.showMenu ||
      prevProps.joinRenewCta !== this.props.joinRenewCta ||
      prevProps.renewCustomerId !== this.props.renewCustomerId
    ) {
      const root = this.hostNode && this.hostNode.shadowRoot;
      if (root && this.props.showMenu) {
        const logoutItem = findLogoutItem(this.props.menuItems);
        rewriteProductionEngageHostToOrigin(root, LOCATION_ORIGIN);
        patchNfibShadowLinks(root, {
          useLogout: !!logoutItem,
          logoutHref: logoutItem ? logoutItem.ref : '',
          loginHref: buildLoginHref(),
        });
        patchHeaderJoinRenewCtas(root, {
          cta: this.props.joinRenewCta,
          userId: this.props.renewCustomerId,
        });
        insertMyNfibBeforeAuthAnchors(root);
      }
    }

    if (prevProps.showMenu && !this.props.showMenu) {
      this.teardownShadow();
    }
    if (!prevProps.showMenu && this.props.showMenu) {
      this.mountShadow();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.teardownShadow();
  }

  teardownShadow() {
    if (this.cleanupEvents) {
      this.cleanupEvents();
      this.cleanupEvents = null;
    }
    const el = this.hostNode;
    if (el && el.shadowRoot) {
      el.shadowRoot.innerHTML = '';
    }
  }

  mountShadow() {
    const el = this.hostNode;
    if (!el || !this.props.showMenu) {
      return;
    }
    if (el.shadowRoot && el.shadowRoot.children.length > 0) {
      return;
    }

    let html;
    try {
      html =
        typeof nfibHeaderHtml === 'string'
          ? nfibHeaderHtml
          : nfibHeaderHtml.default || '';
    } catch (e) {
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.error('[NfibComHeader] Missing nfibHeaderHtmlString bundle', e);
      }
      return;
    }

    if (!html || !this._isMounted || !this.hostNode) {
      return;
    }

    const shadow = el.shadowRoot || el.attachShadow({ mode: 'open' });
    shadow.innerHTML = '';

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = getMontserratFontsCssUrl();

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = getNfibPublicThemeCssUrl();

    const hostStyle = document.createElement('style');
    hostStyle.textContent = `
      :host {
        display: block;
        width: 100%;
        position: relative;
        z-index: 100;
        overflow: visible;
        padding-top: env(safe-area-inset-top, 0);
      }
      :host(.nfib-header-loading) {
        max-height: 82px;
        overflow: hidden;
      }
      :host > header {
        position: relative;
        z-index: 22;
        overflow: visible;
      }
      @media (max-width: 1279px) {
        #nfib-mobile-menu-trigger,
        .nfib-mobile-menu-trigger {
          display: block;
          max-height: 32px;
          width: auto;
          height: auto;
          object-fit: contain;
          flex-shrink: 0;
        }
      }
      .nfib-mobile-tray-backdrop {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 20;
        background: rgba(0, 0, 0, 0.45);
        cursor: pointer;
      }
      .nfib-mobile-tray-backdrop.block {
        display: block;
      }
      @media (max-width: 1279px) {
        .nav-mobile-container.flex {
          position: fixed !important;
          top: var(--nfib-mobile-tray-top, 112px) !important;
          left: 0 !important;
          right: auto !important;
          bottom: 0 !important;
          width: 80vw !important;
          max-width: 80vw !important;
          height: auto !important;
          max-height: none !important;
          margin: 0 !important;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
          z-index: 21;
        }
      }
    `;

    const wrap = document.createElement('div');
    wrap.innerHTML = rewriteNfibPublicSiteUrlsInHtml(html.trim());
    const headerEl = wrap.querySelector('header');

    // the theme stylesheet loads async, but the header markup is inserted
    // immediately - without this, every load shows a flash of the raw,
    // unstyled markup (worse on slow connections) until the CSS arrives.
    // Keep it invisible (but still laid out, via a capped height instead of
    // display:none) until the stylesheet actually finishes loading.
    if (headerEl) {
      headerEl.style.visibility = 'hidden';
      el.classList.add('nfib-header-loading');

      let revealed = false;
      const reveal = () => {
        if (revealed) {
          return;
        }
        revealed = true;
        headerEl.style.visibility = '';
        el.classList.remove('nfib-header-loading');
      };
      link.addEventListener('load', reveal);
      link.addEventListener('error', reveal);
      // fail-safe in case the load/error events never fire
      setTimeout(reveal, 2000);
    }

    shadow.appendChild(fontLink);
    shadow.appendChild(link);
    shadow.appendChild(hostStyle);
    if (headerEl) {
      shadow.appendChild(headerEl);
    } else {
      while (wrap.firstChild) {
        shadow.appendChild(wrap.firstChild);
      }
    }

    rewriteProductionEngageHostToOrigin(shadow, LOCATION_ORIGIN);

    const logoutItem = findLogoutItem(this.props.menuItems);
    patchNfibShadowLinks(shadow, {
      useLogout: !!logoutItem,
      logoutHref: logoutItem ? logoutItem.ref : '',
      loginHref: buildLoginHref(),
    });
    patchHeaderJoinRenewCtas(shadow, {
      cta: this.props.joinRenewCta,
      userId: this.props.renewCustomerId,
    });
    insertMyNfibBeforeAuthAnchors(shadow);

    if (this.cleanupEvents) {
      this.cleanupEvents();
    }
    this.cleanupEvents = bindNfibShadowEvents(shadow);
  }

  render() {
    if (!this.props.showMenu) {
      return null;
    }

    return (
      <div
        ref={this.setHostRef}
        className="nfib-public-header-host"
        style={{ overflow: 'visible' }}
      />
    );
  }
}

function mapStateToProps(state) {
  const currentUser = getCurrentUser(state) || {};
  const userId = currentUser.id || null;
  const customer = userId ? getCustomer(state, { id: userId }) : null;
  const orgKey =
    customer && customer.organization && customer.organization.key;
  // getOrgWithMemberStatus always returns a truthy object (it spreads the
  // raw org into a new one), even before the org has actually loaded - use
  // the raw selector to detect "still loading" instead
  const rawOrganization = orgKey ? getOrganization(state, { id: orgKey }) : null;
  const organization = orgKey
    ? getOrgWithMemberStatus(state, { id: orgKey })
    : null;

  // don't guess "Join Now" while the customer/org fetch is still in
  // flight - stay hidden until we actually know the real membership state
  let joinRenewCta = JoinRenewCta.Join;
  if (userId) {
    if (!customer) {
      joinRenewCta = JoinRenewCta.None;
    } else if (!orgKey) {
      joinRenewCta = JoinRenewCta.Join;
    } else if (!rawOrganization) {
      joinRenewCta = JoinRenewCta.None;
    } else {
      joinRenewCta = computeJoinRenewCta(organization.membershipDetails);
    }
  }

  return {
    joinRenewCta,
    renewCustomerId: userId,
  };
}

export default connect(mapStateToProps)(NfibComHeader);



// WEBPACK FOOTER //
// ./src/common/components/Layout/NfibSiteChrome/NfibComHeader.js